import Text, { TextSize, TextWeight } from "@galaxy-io/dls/text/Text";
import type { PeopleDirectory } from "@gtm/contracts";
import { pageNumber } from "@gtm/contracts";
import { peopleSortKeys } from "@gtm/contracts/core/sorting";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { usePathname, useRouter, useUrlState } from "@/app/navigation";
import { FeedViewport, Inline } from "@/components/workspace/people-styles";
import { RecordTable } from "@/components/workspace/record-table";
import { RelativeTime } from "@/components/workspace/relative-time";
import { SourceStack } from "@/components/workspace/source-icon";
import { useTableSort } from "@/components/workspace/use-table-sort";
import { CompanyLink } from "@/features/companies/company-link";
import { PersonLink } from "@/features/people/person-link";
import { Avatar } from "@/features/people/profile-drawer";
import { apiClient, unwrap } from "@/lib/api-client";
import { sourceLabel } from "@/lib/source-label";
export function PeopleTable({ query }: { query: Record<string, string | undefined> }) {
  const sort = useTableSort(peopleSortKeys, { server: true });
  const page = pageNumber(query.page);
  const { update: openDrawer } = useUrlState();
  const params = new URLSearchParams();
  for (const key of [
    "q",
    "filter",
    "state",
    "tags",
    "assignee",
    "page",
    "days",
    "repository",
    "companyId",
  ])
    if (query[key]) params.set(key, query[key]);
  if (query.company) params.set("companyId", query.company);
  if (sort.query.sort) {
    params.set("sort", sort.query.sort);
    params.set("direction", sort.query.direction!);
  }
  const signature = params.toString();
  const client = useQueryClient();
  const result = useQuery<PeopleDirectory>({
    queryKey: ["people", signature],
    queryFn: ({ signal }) =>
      unwrap(
        apiClient().GET("/people", {
          params: { query: Object.fromEntries(params) },
          signal,
        }),
      ),
    retry: false,
  });
  useEffect(() => {
    const update = () => {
      void client.invalidateQueries({ queryKey: ["people"] });
    };
    window.addEventListener("records:changed", update);
    return () => window.removeEventListener("records:changed", update);
  }, [client]);
  const items = result.error ? [] : result.data?.items || [],
    total = result.data?.total || 0;
  const router = useRouter(),
    path = usePathname();
  function go(next: number) {
    const params = new URLSearchParams(
      Object.entries(query).filter((entry): entry is [string, string] => entry[1] !== undefined),
    );
    params.set("page", String(next));
    router.push(`${path}?${params}`);
  }
  return (
    <FeedViewport>
      <RecordTable
        sort={sort}
        auditKind="person"
        rowKeys={items.map((p) => p.id)}
        editableStatus
        deliveryActions
        isLoading={result.isFetching || result.isPending || sort.pending}
        error={result.error || undefined}
        onRetry={() => void result.refetch()}
        onRowClick={(id) =>
          openDrawer({
            personId: id,
            companyId: null,
            companyTab: null,
            companyPage: null,
            person: null,
            personTab: null,
            activityId: null,
            runId: null,
          })
        }
        key={signature}
        selectBatch={() =>
          unwrap(
            apiClient().GET("/people/selection", {
              params: { query: Object.fromEntries(params) },
            }),
          )
        }
        paging={{
          page,
          total,
          busy: result.isFetching || !!result.error,
          hasPrevious: page > 1,
          hasNext: page * 40 < total,
          onPrevious: () => go(page - 1),
          onNext: () => go(page + 1),
        }}
        label="People directory"
        reviewKeys={items.map((p) => p.review_key)}
        columns={[
          { key: "person", label: "Person" },
          { key: "company", label: "Company", width: 180 },
          { key: "tags", label: "Tags", width: 220 },
          { key: "location", label: "Location", width: 180 },
          { key: "latest", label: "Latest activity", width: "auto" },
          { key: "sources", label: "Sources", width: 220 },
        ]}
        rows={items.map((p) => ({
          person: (
            <PersonLink id={p.id}>
              <Inline>
                <Avatar src={p.avatar} name={p.login} small />
                <Text size={TextSize.BODY_MD} weight={TextWeight.MEDIUM} lineClamp={1}>
                  {p.name || p.login}
                </Text>
              </Inline>
            </PersonLink>
          ),
          company:
            p.company_id && p.company ? (
              <CompanyLink id={p.company_id}>{p.company}</CompanyLink>
            ) : (
              p.company
            ),
          tags: p.tags?.map((tag) => tag.name).join(", ") || "—",
          location: p.location,
          latest: p.latest_activity ? <RelativeTime value={p.latest_activity.at} /> : "—",
          sources: (
            <SourceStack
              sources={p.source_contexts.map((s) => ({
                provider: s.provider,
                label: sourceLabel(s.provider, s.name),
              }))}
            />
          ),
        }))}
      />
    </FeedViewport>
  );
}
